import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "../../../components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"

interface StreamGapMitigationConfig {
  tool_execution_timeout_seconds: number
  max_tool_iterations: number
  web_content_indexing_timeout_seconds: number
  web_content_retrieval_timeout_seconds: number
}

export type { StreamGapMitigationConfig }

interface EditStreamGapMitigationDialogProps {
  isOpen: boolean
  config: StreamGapMitigationConfig
  onSave: (config: StreamGapMitigationConfig) => Promise<void>
  onOpenChange: (open: boolean) => void
}

export function EditStreamGapMitigationDialog({
  isOpen,
  config,
  onSave,
  onOpenChange,
}: EditStreamGapMitigationDialogProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [timeoutSeconds, setTimeoutSeconds] = useState(25)
  const [maxToolIterations, setMaxToolIterations] = useState(6)
  const [webIndexingTimeoutSeconds, setWebIndexingTimeoutSeconds] = useState(35)
  const [webRetrievalTimeoutSeconds, setWebRetrievalTimeoutSeconds] = useState(20)

  useEffect(() => {
    setTimeoutSeconds(config.tool_execution_timeout_seconds)
    setMaxToolIterations(config.max_tool_iterations)
    setWebIndexingTimeoutSeconds(config.web_content_indexing_timeout_seconds)
    setWebRetrievalTimeoutSeconds(config.web_content_retrieval_timeout_seconds)
  }, [config])

  const handleSubmit = async () => {
    if (timeoutSeconds < 0) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.timeoutMinError", {
          defaultValue: "Timeout must be 0 or greater",
        })
      )
      return
    }
    if (timeoutSeconds > 300) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.timeoutMaxError", {
          defaultValue: "Timeout must be 300 seconds or less",
        })
      )
      return
    }
    if (webIndexingTimeoutSeconds < 0) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.webIndexingTimeoutMinError", {
          defaultValue: "Web indexing timeout must be 0 or greater",
        })
      )
      return
    }
    if (webIndexingTimeoutSeconds > 300) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.webIndexingTimeoutMaxError", {
          defaultValue: "Web indexing timeout must be 300 seconds or less",
        })
      )
      return
    }
    if (webRetrievalTimeoutSeconds < 0) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.webRetrievalTimeoutMinError", {
          defaultValue: "Web retrieval timeout must be 0 or greater",
        })
      )
      return
    }
    if (webRetrievalTimeoutSeconds > 300) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.webRetrievalTimeoutMaxError", {
          defaultValue: "Web retrieval timeout must be 300 seconds or less",
        })
      )
      return
    }
    if (maxToolIterations < 0) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.maxTurnsMinError", {
          defaultValue: "Number of turns must be 0 or greater",
        })
      )
      return
    }
    if (maxToolIterations > 20) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.maxTurnsMaxError", {
          defaultValue: "Number of turns must be 20 or less",
        })
      )
      return
    }

    setSaving(true)
    try {
      await onSave({
        tool_execution_timeout_seconds: timeoutSeconds,
        max_tool_iterations: maxToolIterations,
        web_content_indexing_timeout_seconds: webIndexingTimeoutSeconds,
        web_content_retrieval_timeout_seconds: webRetrievalTimeoutSeconds,
      })
      toast.success(
        t("adminDashboard.features.streamGapMitigation.success", {
          defaultValue: "Configuration updated",
        })
      )
      onOpenChange(false)
    } catch (error) {
      toast.error(
        t("adminDashboard.features.streamGapMitigation.error", {
          defaultValue: "Failed to update configuration",
        }) + (error instanceof Error ? `: ${error.message}` : "")
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.features.streamGapMitigation.configTitle", {
              defaultValue: "Streaming Gap Mitigation",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.features.streamGapMitigation.configDescription", {
              defaultValue: "Control tool timeout and maximum number of tool turns.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label htmlFor="tool-timeout-seconds">
              {t("adminDashboard.features.streamGapMitigation.timeoutLabel", {
                defaultValue: "Tool timeout (seconds)",
              })}
            </Label>
            <Input
              id="tool-timeout-seconds"
              min="0"
              step="1"
              type="number"
              value={timeoutSeconds}
              onChange={(e) => {
                const parsed = Number(e.target.value)
                setTimeoutSeconds(Number.isNaN(parsed) ? 0 : parsed)
              }}
            />
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.features.streamGapMitigation.timeoutHelp", {
                defaultValue: "Set to 0 for no timeout.",
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max-tool-iterations">
              {t("adminDashboard.features.streamGapMitigation.maxTurnsLabel", {
                defaultValue: "Number of turns",
              })}
            </Label>
            <Input
              id="max-tool-iterations"
              min="0"
              step="1"
              type="number"
              value={maxToolIterations}
              onChange={(e) => {
                const parsed = Number(e.target.value)
                setMaxToolIterations(Number.isNaN(parsed) ? 0 : parsed)
              }}
            />
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.features.streamGapMitigation.maxTurnsHelp", {
                defaultValue:
                  "Maximum tool-loop turns before forcing final answer. Use 0 for no limit.",
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="web-indexing-timeout-seconds">
              {t("adminDashboard.features.streamGapMitigation.webIndexingTimeoutLabel", {
                defaultValue: "Web indexing timeout (seconds)",
              })}
            </Label>
            <Input
              id="web-indexing-timeout-seconds"
              min="0"
              step="1"
              type="number"
              value={webIndexingTimeoutSeconds}
              onChange={(e) => {
                const parsed = Number(e.target.value)
                setWebIndexingTimeoutSeconds(Number.isNaN(parsed) ? 0 : parsed)
              }}
            />
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.features.streamGapMitigation.webIndexingTimeoutHelp", {
                defaultValue: "Set to 0 for no timeout.",
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="web-retrieval-timeout-seconds">
              {t("adminDashboard.features.streamGapMitigation.webRetrievalTimeoutLabel", {
                defaultValue: "Web retrieval timeout (seconds)",
              })}
            </Label>
            <Input
              id="web-retrieval-timeout-seconds"
              min="0"
              step="1"
              type="number"
              value={webRetrievalTimeoutSeconds}
              onChange={(e) => {
                const parsed = Number(e.target.value)
                setWebRetrievalTimeoutSeconds(Number.isNaN(parsed) ? 0 : parsed)
              }}
            />
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.features.streamGapMitigation.webRetrievalTimeoutHelp", {
                defaultValue: "Set to 0 for no timeout.",
              })}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button disabled={saving} variant="outline" onClick={() => onOpenChange(false)}>
            {t("adminDashboard.features.streamGapMitigation.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button disabled={saving} onClick={handleSubmit}>
            {saving
              ? t("adminDashboard.features.streamGapMitigation.saving", {
                  defaultValue: "Saving...",
                })
              : t("adminDashboard.features.streamGapMitigation.save", {
                  defaultValue: "Save",
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
